import { lowerCase } from 'lodash-es';
import { TranslateService } from '@ngx-translate/core';
import { Frequency, Maybe, ScheduleConfiguration } from '@iss/libs/graphql';
import {
  ChangeDetectorRef,
  Inject,
  LOCALE_ID,
  Pipe,
  PipeTransform,
} from '@angular/core';
import { DATE_PIPE_DEFAULT_OPTIONS, DatePipe, DatePipeConfig } from '@angular/common';

import { RecurrenceDayOfWeekPipe } from '../components/recurrence-schedule/recurrence-day-of-week.pipe';

import { MonthlyRecurrenceTypePipe } from './monthly-recurrence-type.pipe';

import { RecurrenceScheduleDayPipe } from '@iss/wo-common/components';

@Pipe({ name: 'recurrence', pure: false, standalone: true })
export class RecurrencePipe implements PipeTransform {
  private readonly datePipe: DatePipe;
  private readonly dayOfWeekPipe: RecurrenceDayOfWeekPipe;
  private readonly monthlyRecurrenceDayPipe: RecurrenceScheduleDayPipe;
  private readonly monthlyRecurrenceTypePipe: MonthlyRecurrenceTypePipe;

  public constructor(
  @Inject(LOCALE_ID) localeId: string,
    @Inject(DATE_PIPE_DEFAULT_OPTIONS) defaultOptions: DatePipeConfig,
    translate: TranslateService,
    cdr: ChangeDetectorRef,
  ) {
    this.datePipe = new DatePipe(localeId, undefined, defaultOptions);
    this.dayOfWeekPipe = new RecurrenceDayOfWeekPipe(localeId, defaultOptions);
    this.monthlyRecurrenceDayPipe = new RecurrenceScheduleDayPipe(localeId, defaultOptions, translate, cdr);
    this.monthlyRecurrenceTypePipe = new MonthlyRecurrenceTypePipe(translate, cdr);
  }

  public transform(value: Maybe<ScheduleConfiguration>, format: 'short' | 'long' = 'long'): string | null {
    if (!value) {
      return null;
    }

    const from = this.datePipe.transform(value.startDate) || '-';
    const to = this.datePipe.transform(value.endDate) ?? '-';

    const fromTo = `from ${ from } to ${ to }`;

    // TODO: translate values
    switch (value.frequency) {
      case Frequency.Daily:
        const day = value.recurrenceCounter
          ? value.recurrenceCounter === 1 ? 'Everyday' : `Every ${value.recurrenceCounter} days`
          : 'Every weekday';
        return format === 'short' ? day : `${ day } ${ fromTo }`;
      case Frequency.Weekly:
        const week = value.recurrenceCounter === 1 ? 'Every week' : `Every ${ value.recurrenceCounter! } weeks`;
        const days = value.weeklyRecurrenceDayNames?.map(day => this.dayOfWeekPipe.transform(day))?.filter(Boolean).join(', ') ?? '-';
        return format ==='short' ? `${ week } on ${ days }` : `${ week } on ${ days } ${fromTo}`;
      case Frequency.Monthly:
        const month = value.recurrenceCounter === 1 ? 'every month' : `every ${ value.recurrenceCounter! } months`;
        const dayNumber = value.monthlyRecurrenceDayNumber || '-';

        if (value.monthlyRecurrenceConfig) {
          const type = this.monthlyRecurrenceTypePipe.transform(value.monthlyRecurrenceConfig.monthlyRecurrenceConfigType);
          const day = lowerCase(this.monthlyRecurrenceDayPipe.transform(value.monthlyRecurrenceConfig.monthlyRecurrenceConfigDay, false) || '-');
          return format === 'short' ? `${ type } ${ day } of ${ month }` : `${ type } ${ day } of ${ month } ${fromTo}`;
        }

        return format === 'short' ? `Day ${ dayNumber || '-' } of ${ month }` : `Day ${ dayNumber || '-' } of ${ month } ${fromTo}`;
    }
  }
}
